import { useEffect, useState } from 'react';
import { Sparkles, Plus, Pencil, Power } from 'lucide-react';
import { ChargingAnimation } from '@evi/ui';
import {
  fetchAdminUnlocks, createAdminUnlock, updateAdminUnlock, toggleAdminUnlock,
  type AdminUnlock, type AdminUnlockUpsertBody, type ThemeUnlockKind,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './AdminList.module.css';

/**
 * Unlocks CMS — CRUD for the driver-facing catalogue. Structurally
 * mirrors the Coupons page: list + create/edit modal + toggle-active.
 *
 * `code` and `category` are immutable once saved (the API rejects
 * changes) — a rename breaks UserUnlock references, and category
 * changes confuse the ThemeProvider skin detection.
 */
export function UnlocksPage() {
  useRecentRoute('Unlocks');
  const [rows, setRows] = useState<AdminUnlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUnlock | undefined>();

  const load = async () => {
    setLoading(true); setError('');
    try { setRows(await fetchAdminUnlocks()); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onToggle = async (row: AdminUnlock) => {
    try { await toggleAdminUnlock(row.id); await load(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Sparkles size={18} /></div>
          <div>
            <h1 className={styles.title}>Unlocks</h1>
            <p className={styles.subtitle}>{rows.length} unlock{rows.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => { setEditing(undefined); setModalOpen(true); }}
        >
          <Plus size={14} /> New unlock
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <p className={styles.loading}>Loading…</p>}

      {!loading && (
        <div className={styles.grid}>
          {rows.map((u) => (
            <UnlockCard
              key={u.id}
              unlock={u}
              onEdit={() => { setEditing(u); setModalOpen(true); }}
              onToggle={() => void onToggle(u)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit ${editing.name}` : 'New unlock'}
        size="md"
      >
        <UnlockForm
          editing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); void load(); }}
        />
      </Modal>
    </div>
  );
}

/**
 * Short text label for the card's criteria badge. Returns the
 * milestone shorthand ("50 kWh") or the kind name itself for
 * non-milestone categories. Returns "admin" when criteria is unset
 * (matches the PWA picker's read-path which hides un-criteria-fied
 * skins from non-super_admin drivers).
 */
function criteriaLabel(unlock: AdminUnlock): string {
  switch (unlock.unlockCriteriaKind) {
    case 'default': return 'default';
    case 'kwh_milestone': return `≥ ${unlock.unlockCriteriaThreshold ?? 0} kWh`;
    case 'session_milestone': return `≥ ${unlock.unlockCriteriaThreshold ?? 0} sessions`;
    case 'fleet_only': return 'fleet';
    case 'admin_only': return 'admin';
    case null:
    default: return 'admin';
  }
}

function UnlockCard({
  unlock, onEdit, onToggle,
}: {
  unlock: AdminUnlock; onEdit: () => void; onToggle: () => void;
}) {
  return (
    <div className={`${styles.card} ${!unlock.isActive ? styles.cardInactive : ''}`}>
      <UnlockPreview unlock={unlock} />
      <div className={styles.cardHead}>
        <div className={styles.cardBody}>
          <p className={styles.cardTitle}>{unlock.name}</p>
          <code className={styles.cardCode}>{unlock.code}</code>
        </div>
        <span className={`${styles.badge} ${unlock.isActive ? styles.badgeOk : styles.badgeMuted}`}>
          {unlock.isActive ? 'Active' : 'Off'}
        </span>
      </div>
      <p className={styles.cardDesc}>{unlock.description || '—'}</p>
      <div className={styles.cardMeta}>
        <span>{unlock.category}</span>
        <span>{unlock.pricePoints.toLocaleString()} pts</span>
        <span>{unlock.ownedCount} owned</span>
        {unlock.tenantScoped && <span>tenant-scoped</span>}
        {unlock.category === 'skin' && (
          <span style={{
            padding: '1px 6px', borderRadius: 4,
            background: 'color-mix(in srgb, var(--evi-accent, #21c163) 16%, transparent)',
            color: 'var(--evi-accent, #21c163)',
            fontSize: 11, fontWeight: 600,
          }}>
            {criteriaLabel(unlock)}
          </span>
        )}
      </div>
      <div className={styles.cardActions}>
        <button type="button" className={styles.ghostBtn} onClick={onEdit}>
          <Pencil size={12} /> Edit
        </button>
        <button type="button" className={styles.ghostBtn} onClick={onToggle}>
          <Power size={12} /> {unlock.isActive ? 'Disable' : 'Enable'}
        </button>
      </div>
    </div>
  );
}

// ── Preview block ─────────────────────────────────────────────────

/**
 * Inline twin preview on each unlock card. For skin-category
 * unlocks this renders the per-theme ChargingAnimation from @evi/ui
 * in BOTH light and dark variants side-by-side so admins can review
 * how the skin looks in each mode without leaving the page. Colours
 * pass through via accentOverride / chargingOverride so the preview
 * paints the skin's own palette regardless of the admin's active
 * theme.
 *
 * Non-skin unlocks (priority_booking, lounge_access, etc.) fall back
 * to the legacy gradient swatch since ChargingAnimation isn't
 * meaningful for non-theme inventory.
 */
function UnlockPreview({ unlock }: { unlock: AdminUnlock }) {
  if (unlock.category !== 'skin') {
    const meta = unlock.metadata as Record<string, unknown>;
    const a = (meta['accentColor'] as string | undefined) ?? '#888';
    const b = (meta['chargingColor'] as string | undefined) ?? a;
    return (
      <div
        className={styles.swatch}
        style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
        aria-hidden
      />
    );
  }

  const variants = unlock.metadata['variants'] as
    | {
        light?: Record<string, unknown>;
        dark?: Record<string, unknown>;
      }
    | undefined;
  // Fall-through to legacy flat fields if a skin doc hasn't been
  // migrated to the dual-mode shape. Force-apply migration covers
  // the starter skins but tenant-custom skins could still be on the
  // old shape; preview stays functional either way.
  const legacyAccent = unlock.metadata['accentColor'] as string | undefined;
  const legacyCharging = unlock.metadata['chargingColor'] as string | undefined;

  const lightAccent =
    (variants?.light?.['accentColor'] as string | undefined)
    ?? legacyAccent ?? '#888';
  const lightCharging =
    (variants?.light?.['chargingColor'] as string | undefined)
    ?? legacyCharging ?? lightAccent;
  const darkAccent =
    (variants?.dark?.['accentColor'] as string | undefined)
    ?? legacyAccent ?? '#888';
  const darkCharging =
    (variants?.dark?.['chargingColor'] as string | undefined)
    ?? legacyCharging ?? darkAccent;

  return (
    <div className={styles.previewRow} aria-hidden>
      <PreviewCell
        label="Light"
        code={unlock.code}
        mode="light"
        accent={lightAccent}
        charging={lightCharging}
      />
      <PreviewCell
        label="Dark"
        code={unlock.code}
        mode="dark"
        accent={darkAccent}
        charging={darkCharging}
      />
    </div>
  );
}

function PreviewCell({
  label, code, mode, accent, charging,
}: {
  label: string;
  code: string;
  mode: 'light' | 'dark';
  accent: string;
  charging: string;
}) {
  return (
    <div
      className={`${styles.previewCell} ${mode === 'light' ? styles.previewLight : styles.previewDark}`}
      data-mode={mode}
    >
      <span className={styles.previewLabel}>{label}</span>
      <div className={styles.previewAnim}>
        {/*
          Fixed percent (60) so the animation reads as "mid-charge"
          regardless of real session state. `active={false}` keeps
          the wrapper glow on the subtle (non-breathing) state so
          the grid of cards isn't 20 cards all pulsing at once.
          Dispatcher falls through to DefaultAnimation when the
          code isn't in the MODULES table, so this safely renders
          legacy / tenant-custom skin codes too.
        */}
        <ChargingAnimation
          themeCode={code}
          mode={mode}
          percent={60}
          active={false}
          size={120}
          glow="subtle"
          accentOverride={accent}
          chargingOverride={charging}
        />
      </div>
    </div>
  );
}

function UnlockForm({
  editing, onClose, onSaved,
}: {
  editing?: AdminUnlock; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<AdminUnlockUpsertBody>(() => ({
    code: editing?.code ?? '',
    name: editing?.name ?? '',
    description: editing?.description ?? '',
    category: editing?.category ?? 'skin',
    pricePoints: editing?.pricePoints ?? 1000,
    // New-unlock scaffold uses the dual-mode shape so admins creating
    // a skin today produce a doc the resolver can read without
    // legacy-fallback. Existing skins edit their own metadata verbatim.
    metadata: editing?.metadata ?? {
      themeId: 'default',
      variants: {
        dark:  { accentColor: '#3B82F6', chargingColor: '#60A5FA' },
        light: { accentColor: '#3B82F6', chargingColor: '#60A5FA' },
      },
    },
    isActive: editing?.isActive ?? true,
    tenantScoped: editing?.tenantScoped ?? false,
    unlockCriteriaKind: editing?.unlockCriteriaKind ?? null,
    unlockCriteriaThreshold: editing?.unlockCriteriaThreshold ?? null,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setMeta = (key: string, value: string): void => {
    setForm((f) => ({ ...f, metadata: { ...(f.metadata ?? {}), [key]: value } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      if (editing) {
        await updateAdminUnlock(editing.id, {
          name: form.name, description: form.description,
          pricePoints: form.pricePoints, metadata: form.metadata,
          isActive: form.isActive,
          unlockCriteriaKind: form.unlockCriteriaKind,
          unlockCriteriaThreshold: form.unlockCriteriaThreshold,
        });
      } else {
        await createAdminUnlock(form);
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <form className={formStyles.form} onSubmit={handleSubmit}>
      {err && <div className={formStyles.formError}>{err}</div>}
      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Code</label>
          <input
            className={formStyles.input}
            value={form.code}
            disabled={Boolean(editing)}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
          />
          <p className={formStyles.hint}>Immutable once saved.</p>
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Name</label>
          <input
            className={formStyles.input}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label}>Description</label>
        <input
          className={formStyles.input}
          value={form.description ?? ''}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Category</label>
          <select
            className={formStyles.input}
            value={form.category}
            disabled={Boolean(editing)}
            onChange={(e) => setForm({ ...form, category: e.target.value as AdminUnlock['category'] })}
          >
            <option value="skin">Skin</option>
            <option value="priority_booking">Priority booking</option>
            <option value="lounge_access">Lounge access</option>
          </select>
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Price (points)</label>
          <input
            type="number" min={0}
            className={formStyles.input}
            value={form.pricePoints}
            onChange={(e) => setForm({ ...form, pricePoints: Number(e.target.value) })}
            required
          />
        </div>
      </div>

      {form.category === 'skin' && (
        <>
        {/* Theme milestone criteria — how non-fleet drivers unlock
            this skin. Fleet drivers always get every skin at
            attribution (Phase 3a), independent of this setting. */}
        <div className={formStyles.row}>
          <div className={formStyles.field}>
            <label className={formStyles.label}>Unlock criteria</label>
            <select
              className={formStyles.input}
              value={form.unlockCriteriaKind ?? ''}
              onChange={(e) => {
                const k = e.target.value as ThemeUnlockKind | '';
                setForm((f) => ({
                  ...f,
                  unlockCriteriaKind: k === '' ? null : k,
                  // Reset threshold when switching away from milestone kinds.
                  unlockCriteriaThreshold:
                    k === 'kwh_milestone' || k === 'session_milestone'
                      ? (f.unlockCriteriaThreshold ?? 50)
                      : null,
                }));
              }}
            >
              <option value="">— (admin/super_admin only)</option>
              <option value="default">Default (every user at registration)</option>
              <option value="kwh_milestone">kWh milestone</option>
              <option value="session_milestone">Session milestone</option>
              <option value="fleet_only">Fleet perk only</option>
              <option value="admin_only">Admin-only (hidden in PWA)</option>
            </select>
            <p className={formStyles.hint}>
              Non-fleet drivers unlock based on this rule. Fleet drivers get all skins at attribution.
            </p>
          </div>
          {(form.unlockCriteriaKind === 'kwh_milestone'
            || form.unlockCriteriaKind === 'session_milestone') && (
            <div className={formStyles.field}>
              <label className={formStyles.label}>
                Threshold ({form.unlockCriteriaKind === 'kwh_milestone' ? 'kWh' : 'sessions'})
              </label>
              <input
                type="number" min={0} step={1}
                className={formStyles.input}
                value={form.unlockCriteriaThreshold ?? 0}
                onChange={(e) => setForm({
                  ...form,
                  unlockCriteriaThreshold: Number(e.target.value),
                })}
              />
            </div>
          )}
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.field}>
            <label className={formStyles.label}>Theme</label>
            <select
              className={formStyles.input}
              value={(form.metadata?.['themeId'] as string) ?? 'default'}
              onChange={(e) => setMeta('themeId', e.target.value)}
            >
              <option value="default">Default</option>
              <option value="luce">Luce</option>
            </select>
          </div>
          {/*
            TODO Step 9 — both-mode editor.
            Skins now carry a light AND a dark variant under
            metadata.variants.{light,dark}. The inputs below would need
            to expose each variant separately (4 colour pickers +
            optional surface/bg per mode). For this commit we disable
            the single-value inputs so admins can't accidentally write
            a legacy flat shape that the resolver would then fall back
            on. The real dual-mode editor lands alongside the inline
            animation previews in Step 9.
          */}
          <div className={formStyles.field}>
            <label className={formStyles.label}>Accent / Charging</label>
            <input
              className={formStyles.input}
              value="Both-mode editing coming soon"
              disabled
              readOnly
            />
            <p className={formStyles.hint}>
              Each skin now carries separate light and dark colour sets.
              The dual-mode editor lands when inline previews do.
            </p>
          </div>
          <div className={formStyles.field}>
            <label className={formStyles.label}>Tier minimum</label>
            <select
              className={formStyles.input}
              value={(form.metadata?.['tierMinimum'] as string) ?? ''}
              onChange={(e) => setMeta('tierMinimum', e.target.value)}
            >
              <option value="">—</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
              <option value="diamond">Diamond</option>
            </select>
          </div>
        </div>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className={formStyles.btnPrimary} disabled={saving || !form.code || !form.name}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Create'}
        </button>
      </div>
    </form>
  );
}
